import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";
import { Extension } from "@tiptap/react";
import { breadcrumb, captureError } from "@/lib/observability";
import type {
  SpellcheckMatch,
  SpellcheckOpenMatchPayload,
  SpellcheckProofreader,
  SpellcheckStatus,
} from "../types";

export const spellcheckPluginKey = new PluginKey<SpellcheckPluginState>("spellcheck");

const LINT_FAILURE_BACKOFF_MS = 30_000;
const LINT_FAILURE_CAPTURE_THRESHOLD = 3;
const SLOW_LINT_THRESHOLD_MS = 50;
let spellcheckCacheEpoch = 0;

interface StoredSpellcheckMatch {
  id: string;
  from: number;
  to: number;
  blockFrom: number;
  blockTo: number;
  match: SpellcheckMatch;
}

interface SpellcheckPluginState {
  decorations: DecorationSet;
  matches: Map<string, StoredSpellcheckMatch>;
}

type SpellcheckMeta =
  | {
      type: "replaceBlock";
      blockFrom: number;
      blockTo: number;
      decorations: Decoration[];
      matches: StoredSpellcheckMatch[];
    }
  | { type: "removeMatch"; id: string }
  | { type: "clear" };

export interface SpellcheckExtensionOptions {
  proofreader: SpellcheckProofreader;
  debounceMs: number;
  className: string;
  onOpenMatch: (payload: SpellcheckOpenMatchPayload | null) => void;
  onStatusChange: (status: SpellcheckStatus) => void;
}

declare module "@tiptap/react" {
  interface Commands<ReturnType> {
    spellcheck: {
      applySpellcheckSuggestion: (id: string, replacement: string) => ReturnType;
      dismissSpellcheckMatch: (id: string) => ReturnType;
      ignoreSpellcheckMatch: (id: string) => ReturnType;
      clearSpellcheck: () => ReturnType;
      refreshSpellcheck: () => ReturnType;
    };
  }
}

const defaultOptions: SpellcheckExtensionOptions = {
  proofreader: {
    lint: async () => [],
    ignore: async () => {},
  },
  debounceMs: 550,
  className: "df-spellcheck-underline",
  onOpenMatch: () => {},
  onStatusChange: () => {},
};

function mapMatches(matches: Map<string, StoredSpellcheckMatch>, tr: Transaction) {
  const next = new Map<string, StoredSpellcheckMatch>();
  for (const [id, match] of matches.entries()) {
    const from = tr.mapping.map(match.from, 1);
    const to = tr.mapping.map(match.to, -1);
    const blockFrom = tr.mapping.map(match.blockFrom, 1);
    const blockTo = tr.mapping.map(match.blockTo, -1);
    if (to > from && blockTo > blockFrom) {
      next.set(id, { ...match, from, to, blockFrom, blockTo });
    }
  }
  return next;
}

function removeBlockRange(state: SpellcheckPluginState, blockFrom: number, blockTo: number) {
  const oldDecorations = state.decorations.find(blockFrom, blockTo);
  const matches = new Map(state.matches);
  for (const [id, match] of matches.entries()) {
    if (match.blockFrom < blockTo && match.blockTo > blockFrom) {
      matches.delete(id);
    }
  }
  return {
    decorations:
      oldDecorations.length > 0 ? state.decorations.remove(oldDecorations) : state.decorations,
    matches,
  };
}

function applyMeta(
  state: SpellcheckPluginState,
  doc: PMNode,
  meta: SpellcheckMeta
): SpellcheckPluginState {
  if (meta.type === "clear") {
    return { decorations: DecorationSet.empty, matches: new Map() };
  }

  if (meta.type === "removeMatch") {
    const existing = state.matches.get(meta.id);
    if (!existing) return state;
    const decorations = state.decorations.find(existing.from, existing.to, (spec) => {
      return (spec as { spellcheckId?: string }).spellcheckId === meta.id;
    });
    const matches = new Map(state.matches);
    matches.delete(meta.id);
    return {
      decorations:
        decorations.length > 0 ? state.decorations.remove(decorations) : state.decorations,
      matches,
    };
  }

  const withoutBlock = removeBlockRange(state, meta.blockFrom, meta.blockTo);
  const matches = new Map(withoutBlock.matches);
  for (const match of meta.matches) {
    matches.set(match.id, match);
  }
  return {
    decorations:
      meta.decorations.length > 0
        ? withoutBlock.decorations.add(doc, meta.decorations)
        : withoutBlock.decorations,
    matches,
  };
}

function collectTextBlocks(doc: PMNode) {
  const blocks: Array<{ node: PMNode; from: number; to: number; text: string }> = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    if (node.type.name === "codeBlock") return false;
    const text = node.textBetween(0, node.content.size, " ", " ");
    if (text.trim().length === 0) return false;
    blocks.push({
      node,
      from: pos + 1,
      to: pos + 1 + node.content.size,
      text,
    });
    return false;
  });
  return blocks;
}

function buildDecorationsForBlock(
  blockFrom: number,
  blockTo: number,
  matches: SpellcheckMatch[],
  className: string
) {
  const decorations: Decoration[] = [];
  const storedMatches: StoredSpellcheckMatch[] = [];

  for (const match of matches) {
    const from = blockFrom + match.offset;
    const to = from + match.length;
    if (from < blockFrom || to > blockTo || to <= from) continue;

    const id = `${blockFrom}:${blockTo}:${match.id}`;
    decorations.push(
      Decoration.inline(
        from,
        to,
        {
          class: className,
          "data-spellcheck-id": id,
        },
        {
          key: `spellcheck:${id}`,
          spellcheckId: id,
        }
      )
    );
    storedMatches.push({
      id,
      from,
      to,
      blockFrom,
      blockTo,
      match: { ...match, id },
    });
  }

  return { decorations, matches: storedMatches };
}

function findTargetElement(target: EventTarget | null): HTMLElement | null {
  if (target instanceof Element) {
    return target.closest<HTMLElement>("[data-spellcheck-id]");
  }
  if (target instanceof Node && target.parentElement) {
    return target.parentElement.closest<HTMLElement>("[data-spellcheck-id]");
  }
  return null;
}

export const SpellcheckExtension = Extension.create<SpellcheckExtensionOptions>({
  name: "spellcheck",

  addOptions() {
    return defaultOptions;
  },

  addCommands() {
    return {
      applySpellcheckSuggestion:
        (id, replacement) =>
        ({ state, dispatch }) => {
          const pluginState = spellcheckPluginKey.getState(state);
          const match = pluginState?.matches.get(id);
          if (!match || !dispatch) return Boolean(match);
          dispatch(
            state.tr
              .insertText(replacement, match.from, match.to)
              .setMeta(spellcheckPluginKey, { type: "removeMatch", id } satisfies SpellcheckMeta)
          );
          this.options.onOpenMatch(null);
          return true;
        },
      dismissSpellcheckMatch:
        (id) =>
        ({ state, dispatch }) => {
          const pluginState = spellcheckPluginKey.getState(state);
          const match = pluginState?.matches.get(id);
          if (!match) return false;
          dispatch?.(
            state.tr.setMeta(spellcheckPluginKey, {
              type: "removeMatch",
              id,
            } satisfies SpellcheckMeta)
          );
          this.options.onOpenMatch(null);
          return true;
        },
      ignoreSpellcheckMatch:
        (id) =>
        ({ state, dispatch }) => {
          const pluginState = spellcheckPluginKey.getState(state);
          const match = pluginState?.matches.get(id);
          if (!match) return false;
          spellcheckCacheEpoch++;
          void this.options.proofreader.ignore(match.match).catch((error: unknown) => {
            breadcrumb(
              "spellcheck.ignore_failed",
              { matchKind: match.match.kind },
              { category: "spellcheck", level: "warning" }
            );
            captureError(error, {
              feature: "spellcheck",
              operation: "ignore_failed",
              extra: { matchKind: match.match.kind },
            });
            this.options.onStatusChange("unavailable");
          });
          dispatch?.(
            state.tr.setMeta(spellcheckPluginKey, {
              type: "removeMatch",
              id,
            } satisfies SpellcheckMeta)
          );
          this.options.onOpenMatch(null);
          return true;
        },
      clearSpellcheck:
        () =>
        ({ state, dispatch }) => {
          dispatch?.(
            state.tr.setMeta(spellcheckPluginKey, { type: "clear" } satisfies SpellcheckMeta)
          );
          this.options.onOpenMatch(null);
          return true;
        },
      refreshSpellcheck:
        () =>
        ({ state, dispatch }) => {
          spellcheckCacheEpoch++;
          dispatch?.(
            state.tr.setMeta(spellcheckPluginKey, { type: "clear" } satisfies SpellcheckMeta)
          );
          this.options.onOpenMatch(null);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const options = this.options;

    return [
      new Plugin<SpellcheckPluginState>({
        key: spellcheckPluginKey,
        state: {
          init: () => ({ decorations: DecorationSet.empty, matches: new Map() }),
          apply: (tr, value, _oldState, newState) => {
            const meta = tr.getMeta(spellcheckPluginKey) as SpellcheckMeta | undefined;
            let next = tr.docChanged
              ? {
                  decorations: value.decorations.map(tr.mapping, tr.doc),
                  matches: mapMatches(value.matches, tr),
                }
              : value;

            if (meta) {
              next = applyMeta(next, newState.doc, meta);
            }

            return next;
          },
        },
        props: {
          decorations(state) {
            return spellcheckPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
          },
          handleClick(view, _pos, event) {
            const element = findTargetElement(event.target);
            if (!element) return false;
            const id = element.dataset.spellcheckId;
            if (!id) return false;
            const match = spellcheckPluginKey.getState(view.state)?.matches.get(id);
            if (!match) return false;

            options.onOpenMatch({
              id,
              problemText: match.match.problemText,
              message: match.match.message,
              suggestions: match.match.suggestions,
              from: match.from,
              to: match.to,
              rect: element.getBoundingClientRect(),
            });
            return true;
          },
        },
        view(view: EditorView) {
          let debounceTimer: ReturnType<typeof setTimeout> | null = null;
          let lintCache = new WeakMap<PMNode, SpellcheckMatch[]>();
          let cacheEpoch = spellcheckCacheEpoch;
          let consecutiveFailures = 0;
          let pausedUntil = 0;
          let runId = 0;
          let destroyed = false;

          const clearTimer = () => {
            if (debounceTimer) {
              clearTimeout(debounceTimer);
              debounceTimer = null;
            }
          };

          const runCheck = () => {
            if (destroyed || !view.editable) return;
            if (Date.now() < pausedUntil) return;
            if (cacheEpoch !== spellcheckCacheEpoch) {
              lintCache = new WeakMap<PMNode, SpellcheckMatch[]>();
              cacheEpoch = spellcheckCacheEpoch;
            }

            const currentRunId = ++runId;
            const docAtStart = view.state.doc;
            const blocks = collectTextBlocks(docAtStart);
            options.onStatusChange(blocks.length > 0 ? "checking" : "ready");

            if (blocks.length === 0) {
              view.dispatch(
                view.state.tr.setMeta(spellcheckPluginKey, {
                  type: "clear",
                } satisfies SpellcheckMeta)
              );
              options.onStatusChange("ready");
              return;
            }

            let runHadFailure = false;
            void Promise.all(
              blocks.map(async (block) => {
                try {
                  const cachedMatches = lintCache.get(block.node);
                  const lintStartedAt = performance.now();
                  const matches = cachedMatches ?? (await options.proofreader.lint(block.text));
                  if (!cachedMatches) {
                    const durationMs = Math.round(performance.now() - lintStartedAt);
                    if (durationMs > SLOW_LINT_THRESHOLD_MS) {
                      breadcrumb(
                        "spellcheck.lint_slow",
                        { durationMs, blockCount: blocks.length },
                        { category: "spellcheck", level: "warning" }
                      );
                    }
                    lintCache.set(block.node, matches);
                  }
                  if (destroyed || currentRunId !== runId) return;
                  if (view.state.doc !== docAtStart) return;
                  const built = buildDecorationsForBlock(
                    block.from,
                    block.to,
                    matches,
                    options.className
                  );
                  view.dispatch(
                    view.state.tr.setMeta(spellcheckPluginKey, {
                      type: "replaceBlock",
                      blockFrom: block.from,
                      blockTo: block.to,
                      decorations: built.decorations,
                      matches: built.matches,
                    } satisfies SpellcheckMeta)
                  );
                } catch (error) {
                  if (!destroyed && currentRunId === runId) {
                    runHadFailure = true;
                    consecutiveFailures++;
                    pausedUntil = Date.now() + LINT_FAILURE_BACKOFF_MS;
                    breadcrumb(
                      "spellcheck.lint_failed",
                      { failureCount: consecutiveFailures, blockCount: blocks.length },
                      { category: "spellcheck", level: "warning" }
                    );
                    if (consecutiveFailures === LINT_FAILURE_CAPTURE_THRESHOLD) {
                      captureError(error, {
                        feature: "spellcheck",
                        operation: "lint_failed",
                        extra: { failureCount: consecutiveFailures, blockCount: blocks.length },
                      });
                    }
                    options.onStatusChange("unavailable");
                  }
                }
              })
            ).then(() => {
              if (!destroyed && currentRunId === runId && !runHadFailure) {
                consecutiveFailures = 0;
                options.onStatusChange("ready");
              }
            });
          };

          const schedule = (delay = options.debounceMs) => {
            clearTimer();
            debounceTimer = setTimeout(runCheck, delay);
          };

          schedule(0);

          return {
            update(_view, prevState) {
              if (cacheEpoch !== spellcheckCacheEpoch) {
                lintCache = new WeakMap<PMNode, SpellcheckMatch[]>();
                cacheEpoch = spellcheckCacheEpoch;
                options.onOpenMatch(null);
                schedule(0);
                return;
              }
              if (_view.state.doc !== prevState.doc) {
                options.onOpenMatch(null);
                schedule();
              }
            },
            destroy() {
              destroyed = true;
              runId++;
              clearTimer();
              options.onOpenMatch(null);
            },
          };
        },
      }),
    ];
  },
});

export const __testOnly = {
  buildDecorationsForBlock,
  collectTextBlocks,
};
